import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ListTable from '../../components/ListTable';
import Container from '../../layouts/Container';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button, Form } from 'react-bootstrap';
import EditRoleModal from '../../components/EditRoleModal';
import { getRoles, deleteRole, generateRole } from '../../services/project/roleService';
import { getAreas } from '../../services/project/areaService';
import { getRegions } from '../../services/project/projectService';
import InputWithButton from '../../components/InputWithButton';
import { Pencil, XOctagon } from 'react-bootstrap-icons';
import AnchorTag from '../../components/AnchorTag';
import { useProjects } from '../../context/ProjectContext';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLeadership, setIsLeadership] = useState(false); // ✅ NEW
  const [trackAttendance, setTrackAttendance] = useState(false);
  const { projectId } = useParams();
  const { isProjectReadOnly } = useProjects();

  useEffect(() => {
    fetchRoles();
    setCurrentUser(JSON.parse(localStorage.getItem('user')));
  }, []);

  useEffect(() => {
    const fetchRegionsAndAreas = async () => {
      const regionResponse = await getRegions(projectId);
      if (!regionResponse.success) {
        toast.error(regionResponse.data?.errorMessage || 'Error fetching regions.');
        return;
      }

      const fetchedRegions = regionResponse.regions;
      setRegions(fetchedRegions);

      const areaResponses = await Promise.all(
        fetchedRegions.map(region => getAreas(projectId, region.id))
      );

      const allAreas = areaResponses
        .filter(resp => resp.success)
        .flatMap(resp => resp.areas || []);

      setAreas(allAreas);
    };

    if (projectId) {
      fetchRegionsAndAreas();
    }
  }, [projectId]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const roleResponse = await getRoles(projectId);
      if (!roleResponse.success) {
        toast.error(roleResponse.data?.errorMessage || 'Error fetching roles.');
        return;
      }
      setRoles(roleResponse.roles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    setShowModal(true);
    setSelectedRole(role);
  };

  const handleDelete = async (roleId) => {
    const confirmed = window.confirm('Are you sure?');
    if (!confirmed) return;
    try {
      const response = await deleteRole(roleId);

      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchRoles();
      toast.success(response.message);
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  // ✅ UPDATED: include isLeadership and trackAttendance in the payload
  const handleGenerateRoles = async (projectId, roleName) => {
    if (!roleName) return toast.error('Role name is required!');
    const confirmed = window.confirm(
      `Are you sure you want to generate "${roleName}"? This action will create ${areas.length} roles${
        isLeadership ? ' (Leadership)' : ''
      }${
        trackAttendance ? ' with Attendance Tracking' : ''
      }.`
    );
    if (!confirmed) return;

    try {
      const response = await generateRole({ projectId, roleName, isLeadership, trackAttendance }); // ✅ pass flags
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchRoles();
      toast.success(response.message);
    } catch (error) {
      console.error('Error generating roles:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor(row => row.name, {
      id: 'name',
      header: 'Name',
      cell: info => info.getValue(),
    }),
    columnHelper.display({
      id: 'scope',
      header: 'Scope',
      cell: info => {
        const role = info.row.original;
        const scope = role?.regionIds;
        if (scope === null) return null;

        return (
          <div className="d-flex flex-column gap-1">
            {scope?.map((sc, idx) => {
              const regionName = regions.find(r => r.id === sc.regionId)?.name || sc.regionId;
              const areaNames = (sc.areas || [])
                .map(areaId => areas.find(a => a.id === areaId)?.name || areaId)
                .join(', ');
              return (
                <div key={idx}>
                  <strong>{regionName}</strong> - {areaNames}
                </div>
              );
            })}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'permissions',
      header: 'Permissions',
      cell: info => {
        const role = info.row.original;
        const permissions = role?.permissions || [];
        return (
          <div className="d-flex flex-column gap-1">
            {Object.entries(permissions).map(([section, actions]) => (
              <div key={section}>
                <strong>{section}</strong> - {actions.join(', ')}
              </div>
            ))}
          </div>
        );
      },
    }),
    // (Optional) If you want to show Leadership in the table, keep this block. Otherwise you can remove it.
    columnHelper.accessor(row => row.isLeadership, {
      id: 'isLeadership',
      header: 'Leadership',
      cell: info =>
        info.getValue() ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>,
    }),
    ( isProjectReadOnly ? false : columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const role = info.row.original;
        if (role.name === 'Admin') return null;
        return (
          <div className="d-flex gap-2">
            <AnchorTag onClick={() => handleEdit(role)} hoverText="Edit">
              <Pencil />
            </AnchorTag>
            <AnchorTag onClick={() => handleDelete(role.id)} hoverText="Delete">
              <XOctagon />
            </AnchorTag>
          </div>
        );
      },
    })),
  ].filter(Boolean);

  return (
    <Container>
      <div className="d-flex align-items-center mb-3 gap-2">

        {/* Right: Leadership toggle + Generate Default Role */}
        {!isProjectReadOnly && (
          <>
          <div className="d-flex align-items-center gap-2">
            <h2 className="mb-0">Roles</h2>
            <Button
              onClick={() => {
                setSelectedRole(null);
                setShowModal(true);
              }}
            >
              Add New
            </Button>
          </div>
          <div className="ms-auto d-flex align-items-center gap-3" style={{ maxWidth: 700, width: '100%' }}>
            {/* ✅ NEW Leadership checkbox */}
            <Form.Check
              type="checkbox"
              id="isLeadership"
              label="Leadership"
              checked={isLeadership}
              onChange={(e) => setIsLeadership(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              id="trackAttendance"
              label="Track Attendance"
              checked={trackAttendance}
              onChange={(e) => setTrackAttendance(e.target.checked)}
            />
            <div style={{ flex: 1 }}>
              <InputWithButton
                placeholder="Default role name..."
                title="Generate Default Role"
                onSubmit={(roleName) => handleGenerateRoles(projectId, roleName)}
              />
            </div>
          </div></>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <ListTable
            data={roles}
            columns={columns}
            filters={[
              { label: 'All Distributors', value: '' },
              ...areas.map(area => ({ label: area.name, value: area.id })),
            ]}
            filterFn={(role, selectedValue) => {
              if (!selectedValue) return true;
              return role.regionIds?.some(r => r.areas?.includes(Number(selectedValue)));
            }}
          />
          {showModal && (
            <EditRoleModal
              role={selectedRole}
              show={showModal}
              refetch={fetchRoles}
              onClose={() => setShowModal(false)}
            />
          )}
        </>
      )}
    </Container>
  );
}

export default Roles;
